/*  tanstack  */
import { CellContext, Row } from '@tanstack/react-table';
import { SSOSupportIcons } from 'application/Shared/Components/SSOSupportIcons';
import { SSOUtils } from 'application/Shared/Helpers';

/*  Shared  */
import { TenantApp } from 'application/Shared/types/tenantApp';

/*  React  */
import React from 'react';

import { ColumnFilter } from '../Table/types';

export enum SsoSupportStatus {
    AvailableNotInUse = 'availableNotInUse',
    AtLeastOneInUse = 'atLeastOneInUse',
    Unavailable = 'unavailable'
}

/**
 * Configuration object for sso support filtering control.
 */
export const ssoSupportFilter: ColumnFilter<unknown> = {
    control: 'Checkbox',
    defaultOptions: [
        {
            options: [
                SsoSupportStatus.AvailableNotInUse,
                SsoSupportStatus.AtLeastOneInUse,
                SsoSupportStatus.Unavailable
            ]
        }
    ]
};

/**
 * Filter function for SSO support column in tables
 * @param {Row<TData>} row - The table row being filtered
 * @param {string} columnId - The ID of the column being filtered
 * @param {string} filterValue - The filter value to check against
 * @returns {boolean} True if the row should be included based on filter, false otherwise
 */
export function ssoSupportFilterFn<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue) return true;

    const filterValueArray = Array.isArray(filterValue)
        ? filterValue
        : [filterValue];

    if (filterValueArray.length === 0) return true;

    const ssoSupport: SsoSupportType[] = row.getValue(columnId);

    if (!ssoSupport) return false;

    const unavailable =
        !ssoSupport[0]?.globalSupport &&
        !ssoSupport[0]?.tenantUse &&
        !ssoSupport[1]?.globalSupport &&
        !ssoSupport[1]?.tenantUse;

    const atLeastOneInUse =
        ssoSupport[0]?.tenantUse || ssoSupport[1]?.tenantUse;

    const availableNotInUse =
        (ssoSupport[0]?.globalSupport && !ssoSupport[0]?.tenantUse) ||
        (ssoSupport[1]?.globalSupport && !ssoSupport[1]?.tenantUse);

    if (unavailable)
        return filterValueArray.includes(SsoSupportStatus.Unavailable);
    if (atLeastOneInUse)
        return filterValueArray.includes(SsoSupportStatus.AtLeastOneInUse);
    if (availableNotInUse)
        return filterValueArray.includes(SsoSupportStatus.AvailableNotInUse);

    return false;
}

export type SsoSupportType = {
    globalSupport: boolean;
    provider: 'google_sso' | 'microsoft_sso';
    tenantUse: boolean;
};

/**
 * Component that renders SSO support icons in a table cell.
 * @param {CellContext<TenantApp, unknown>} props - The cell context props from TanStack table
 * @param {object} props.cell - The cell information containing the SSO support data
 * @param {object} props.row - The row information containing the original tenant app data
 * @param {object} props.rest - Additional props to be passed to the SSOSupportIcons component
 * @returns {JSX.Element} A SSOSupportIcons component with the cell's SSO support data
 */
export const SSOSupportIconsCell = ({
    cell,
    row,
    ...rest
}: CellContext<TenantApp, unknown>): JSX.Element => {
    let ssoSupport = cell.getValue();

    // if the ssoSupport is not available, we try to get it from the row.
    // TODO: Backend should return the ssoSupport in the response!!!
    if (!ssoSupport) {
        ssoSupport = SSOUtils.tenantAppGetSSOSupport(row.original);
    }

    return (
        <SSOSupportIcons
            ssoSupport={ssoSupport as SsoSupportType[]}
            enableTooltip
            tenantApp={row.original}
            {...rest}
        />
    );
};
